import { useRef } from 'react';

/**
 * Request version tracker to detect stale responses.
 *
 * Usage:
 * ```tsx
 * const versionRef = useRef(0);
 *
 * const fetchData = async () => {
 *   const version = ++versionRef.current;
 *   const data = await api.getMdsAssessments(params);
 *
 *   // Ignore if stale
 *   if (version !== versionRef.current) return;
 *
 *   // Process data...
 * };
 * ```
 */
export class RequestVersionTracker {
  private version = 0;

  /**
   * Increment version and return new version number
   */
  next(): number {
    return ++this.version;
  }

  /**
   * Check if version is still current
   */
  isCurrent(version: number): boolean {
    return version === this.version;
  }

  /**
   * Get current version without incrementing
   */
  current(): number {
    return this.version;
  }

  /**
   * Reset version counter
   */
  reset(): void {
    this.version = 0;
  }
}

/**
 * React hook for request version tracking
 */
export function useRequestVersion() {
  const trackerRef = useRef(new RequestVersionTracker());
  return trackerRef.current;
}
